import React, { useState, useEffect, useRef, useCallback } from 'react';
// ChevronDown import removed as it's not used

const NameAutocomplete = ({ value, onChange, placeholder, className, error, disabled }) => {
  const [suggestions, setSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(-1);
  const [allNames, setAllNames] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const wrapperRef = useRef(null);
  const inputRef = useRef(null);

  // Fetch names from API
  useEffect(() => {
    const fetchNames = async () => {
      setIsLoading(true);
      try {
        const response = await fetch('https://names-storage.t9e2i3kal.workers.dev/list');
        if (response.ok) {
          const data = await response.json();
          setAllNames(data || []);
        }
      } catch (error) {
        console.error('Failed to fetch names:', error);
        setAllNames([]);
      } finally {
        setIsLoading(false);
      }
    };

    fetchNames();
  }, []);

  // Filter suggestions based on input
  const filterSuggestions = useCallback((inputValue) => {
    if (!inputValue || inputValue.length < 2) {
      setSuggestions([]);
      return;
    }

    const filtered = allNames
      .filter(name => name.toUpperCase().includes(inputValue.toUpperCase()))
      .map(name => name.toUpperCase())
      .slice(0, 5); // Limit to 5 suggestions

    setSuggestions(filtered);
    setSelectedIndex(-1);
  }, [allNames]);

  // Handle click outside to close dropdown
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target)) {
        setShowSuggestions(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleInputChange = (e) => {
    const newValue = e.target.value.toUpperCase();
    onChange(newValue);
    filterSuggestions(newValue);
    if (newValue.length >= 2) {
      setShowSuggestions(true);
    }
  };

  const handleSuggestionClick = (suggestion) => {
    onChange(suggestion.toUpperCase());
    setShowSuggestions(false);
    setSuggestions([]);
    setSelectedIndex(-1);
  };

  const handleKeyDown = (e) => {
    if (!showSuggestions || suggestions.length === 0) return;

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setSelectedIndex(prev =>
          prev < suggestions.length - 1 ? prev + 1 : prev
        );
        break;
      case 'ArrowUp':
        e.preventDefault();
        setSelectedIndex(prev => prev > -1 ? prev - 1 : -1);
        break;
      case 'Enter':
        e.preventDefault();
        if (selectedIndex >= 0 && selectedIndex < suggestions.length) {
          handleSuggestionClick(suggestions[selectedIndex]);
        }
        break;
      case 'Escape':
        setShowSuggestions(false);
        setSelectedIndex(-1);
        break;
      default:
        // Ignore other keys
        break;
    }
  };

  const handleFocus = () => {
    if (value && value.length >= 2) {
      filterSuggestions(value);
      setShowSuggestions(true);
    }
  };

  // Watch the value prop directly for changes from on-screen keyboard
  useEffect(() => {
    const newValue = value;
    if (newValue && newValue.length >= 2) {
      const filtered = allNames
        .filter(name => name.toUpperCase().includes(newValue.toUpperCase()))
        .map(name => name.toUpperCase())
        .slice(0, 5);
      setSuggestions(filtered);
      setShowSuggestions(true);
    } else {
      setSuggestions([]);
      setShowSuggestions(false);
    }
  }, [value, allNames]);

  const clearInput = () => {
    onChange('');
    setSuggestions([]);
    setShowSuggestions(false);
    setSelectedIndex(-1);
    inputRef.current?.focus();
  };

  return (
    <div className="name-autocomplete-wrapper" ref={wrapperRef}>
      <div className="email-input-wrapper">
        <input
          ref={inputRef}
          type="text"
          value={value}
          onChange={handleInputChange}
          onKeyDown={handleKeyDown}
          onFocus={handleFocus}
          placeholder={placeholder}
          className={`form-input ${error ? 'error' : ''} ${className || ''}`}
          disabled={disabled || isLoading}
        />
        {value && !disabled && (
          <button
            type="button"
            className="clear-name-btn"
            onClick={clearInput}
            aria-label="Clear name"
            disabled={disabled}
          >
            ×
          </button>
        )}
      </div>

      {/* Always visible suggestions list with site theme */}
      {suggestions.length > 0 && (
        <div style={{
          background: '#111',
          border: '2px solid #e6c618',
          borderRadius: '4px',
          marginTop: '5px',
          maxHeight: '200px',
          overflowY: 'auto'
        }}>
          {suggestions.map((suggestion, index) => (
            <div
              key={index}
              onClick={() => handleSuggestionClick(suggestion)}
              onMouseEnter={() => setSelectedIndex(index)}
              style={{
                padding: '12px 15px',
                cursor: 'pointer',
                backgroundColor: selectedIndex === index ? '#e6c618' : 'transparent',
                color: selectedIndex === index ? '#000' : '#fff',
                fontSize: '14px',
                fontWeight: '500',
                borderBottom: index < suggestions.length - 1 ? '1px solid #333' : 'none',
                transition: 'all 0.2s ease'
              }}
            >
              {suggestion}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default NameAutocomplete;